import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { CustomSelect } from '@/components/CustomSelect';
import { Newspaper, AlertTriangle, TrendingUp, TrendingDown, ArrowRight, Zap, Search, ExternalLink, RefreshCw, Bell, Shield } from 'lucide-react';

const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';
const urgColor = { hoog: '#8A3C3C', middel: '#A67C52', laag: '#8C877F' };
const urgBg = { hoog: '#FDF5F5', middel: '#FDF8F0', laag: '#F4F1EB' };
const sentColor = { positief: '#2E6F57', negatief: '#8A3C3C', neutraal: '#8C877F' };
const sentBg = { positief: '#E6EFEA', negatief: '#FDF5F5', neutraal: '#F4F1EB' };

export default function NewsIntelligence() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [themeF, setThemeF] = useState('all');
  const [sentF, setSentF] = useState('all');
  const [urgF, setUrgF] = useState('all');

  const loadData = async () => {
    try { const { data: d } = await api.get('/news/latest'); setData(d); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post('/news/scrape'); await loadData(); }
    catch (e) { console.error(e); } finally { setRefreshing(false); }
  };

  const filtered = useMemo(() => {
    if (!data?.articles) return [];
    let list = data.articles;
    if (search) { const s = search.toLowerCase(); list = list.filter(a => a.title.toLowerCase().includes(s) || a.theme.toLowerCase().includes(s)); }
    if (themeF !== 'all') list = list.filter(a => a.theme === themeF);
    if (sentF !== 'all') list = list.filter(a => a.sentiment === sentF);
    if (urgF !== 'all') list = list.filter(a => a.urgency === urgF);
    return list;
  }, [data, search, themeF, sentF, urgF]);

  const themes = useMemo(() => data?.articles ? [...new Set(data.articles.map(a => a.theme))].sort() : [], [data]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading news...</p></div>;
  if (!data) return <div className="text-center py-20 text-[#8C877F]">No news data available.</div>;

  const snap = data.snapshot || {};
  const alerts = data.alerts || [];
  const topStory = data.articles?.find(a => a.article_id === snap.top_story_id) || data.articles?.[0];
  const regimeColor = snap.macro_regime === 'constructief' ? '#2E6F57' : snap.macro_regime === 'stress' ? '#8A3C3C' : '#A67C52';

  // Theme sentiment aggregation
  const themeSent = {};
  (data.articles || []).forEach(a => {
    if (!themeSent[a.theme]) themeSent[a.theme] = { pos: 0, neg: 0, neu: 0, count: 0 };
    themeSent[a.theme][a.sentiment === 'positief' ? 'pos' : a.sentiment === 'negatief' ? 'neg' : 'neu']++;
    themeSent[a.theme].count++;
  });

  return (
    <div className="space-y-7 animate-fade-in" data-testid="news-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Vastgoed News Intelligence</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Daily News & Sentiment</h1>
          {snap.scraped_at && <p className="text-[10px] text-[#D9D3C8] mt-0.5">Laatst bijgewerkt: {new Date(snap.scraped_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })} — Auto-refresh: dagelijks 06:00</p>}
        </div>
        <button onClick={refresh} disabled={refreshing}
          className="flex items-center gap-1.5 px-4 py-2 border border-[#E2DDD3] text-[#5A5A5A] rounded-lg text-[11px] font-sans font-medium hover:border-[#1F3A34]/30 transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Vernieuwen...' : 'Vernieuwen'}
        </button>
      </div>

      {/* Sentiment Score + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 flex flex-col items-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <svg viewBox="0 0 100 100" className="w-24 h-24">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#F0EDE8" strokeWidth="6" />
            <circle cx="50" cy="50" r="42" fill="none" stroke={sColor(snap.sentiment_score || 50, 60, 40)} strokeWidth="6"
              strokeDasharray={`${2*Math.PI*42}`} strokeDashoffset={`${2*Math.PI*42*(1-(snap.sentiment_score||50)/100)}`}
              strokeLinecap="round" transform="rotate(-90 50 50)" />
            <text x="50" y="48" textAnchor="middle" fill="#1C1C1C" fontSize="20" fontWeight="600" fontFamily="Inter">{snap.sentiment_score || 50}</text>
            <text x="50" y="62" textAnchor="middle" fill="#8C877F" fontSize="8">SENTIMENT</text>
          </svg>
          <p className="text-[14px] font-sans font-semibold mt-1" style={{ color: regimeColor }}>{(snap.macro_regime || 'neutraal').charAt(0).toUpperCase() + (snap.macro_regime || 'neutraal').slice(1)}</p>
          <p className="text-[10px] text-[#8C877F]">Confidence: {snap.confidence_score || 0}%</p>
        </div>
        <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { l: 'Artikelen vandaag', v: snap.total_articles || 0 },
            { l: 'High Priority', v: snap.high_priority_articles || 0, c: '#8A3C3C' },
            { l: 'Alerts', v: alerts.length, c: alerts.length > 0 ? '#8A3C3C' : '#2E6F57' },
            { l: 'Thema\'s', v: themes.length },
            { l: 'Bronnen', v: data.sources?.length || 0 },
          ].map(k => (
            <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-3" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <p className="text-[9px] text-[#8C877F] font-sans font-medium">{k.l}</p>
              <p className="text-[22px] font-sans font-semibold leading-none mt-1" style={{ color: k.c || '#1C1C1C' }}>{k.v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white border border-[#8A3C3C]/15 rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><Bell className="w-4 h-4 text-[#8A3C3C]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">High Impact Alerts ({alerts.length})</p></div>
          {alerts.slice(0, 4).map(a => (
            <div key={a.alert_id} className="flex items-center gap-3 py-2 border-b border-[#F4F1EB] last:border-0">
              <div className={`w-2 h-2 rounded-full ${a.severity === 'high' ? 'bg-[#8A3C3C]' : 'bg-[#A67C52]'}`} />
              <span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium" style={{ backgroundColor: urgBg[a.severity === 'high' ? 'hoog' : 'middel'], color: urgColor[a.severity === 'high' ? 'hoog' : 'middel'] }}>{a.alert_type}</span>
              <p className="text-[12px] text-[#1C1C1C] font-sans flex-1">{a.title}</p>
            </div>
          ))}
        </div>
      )}

      {/* Top Story */}
      {topStory && (
        <div className="bg-white border border-[#1F3A34]/15 rounded-xl p-6 space-y-4" style={{ boxShadow: '0 4px 16px rgba(31,58,52,0.05)' }}>
          <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[12px] text-[#1F3A34] font-sans font-semibold">Belangrijkste Nieuwsitem</p></div>
          <h2 className="font-serif text-[22px] text-[#1C1C1C] font-medium leading-tight">{topStory.title}</h2>
          <div className="flex items-center gap-3 text-[11px] text-[#8C877F]">
            <span>{topStory.source}</span><span>{topStory.published_at}</span>
            <span className="px-2 py-0.5 rounded-lg font-medium" style={{ backgroundColor: sentBg[topStory.sentiment], color: sentColor[topStory.sentiment] }}>{topStory.sentiment}</span>
            <span className="px-2 py-0.5 rounded-lg font-medium" style={{ backgroundColor: urgBg[topStory.urgency], color: urgColor[topStory.urgency] }}>{topStory.urgency}</span>
            <span className="font-semibold text-[#1C1C1C]">Impact: {topStory.impact_score}</span>
          </div>
          <p className="text-[13px] text-[#5A5A5A] font-sans leading-relaxed">{topStory.ai_summary}</p>
          {topStory.portfolio_impact && (
            <div className="bg-[#F4F1EB] rounded-xl p-4 space-y-1.5">
              <p className="text-[11px] text-[#5A5A5A] font-sans font-semibold">Portfolio Impact</p>
              {Object.entries(topStory.portfolio_impact).map(([k, v]) => (
                <div key={k} className="flex items-center gap-3 text-[12px]">
                  <span className="text-[#8C877F] w-28 capitalize">{k}</span>
                  <span className="text-[#1C1C1C] font-sans">{v}</span>
                </div>
              ))}
            </div>
          )}
          {topStory.source_url && <a href={topStory.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[11px] text-[#1F3A34] font-sans font-medium hover:underline"><ExternalLink className="w-3 h-3" /> Lees meer op {topStory.source}</a>}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: News Feed */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[160px] max-w-[240px]">
              <Search className="w-4 h-4 text-[#8C877F] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Zoek nieuws..."
                className="w-full pl-9 pr-3 py-2 bg-[#F4F1EB] border-none rounded-lg text-[13px] font-sans placeholder:text-[#D9D3C8] focus:outline-none focus:ring-1 focus:ring-[#1F3A34]/20" />
            </div>
            <CustomSelect value={themeF} onChange={setThemeF} options={[{ value: 'all', label: 'Alle thema\'s' }, ...themes.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))]} testId="theme-f" />
            <CustomSelect value={sentF} onChange={setSentF} options={[{ value: 'all', label: 'Sentiment' }, { value: 'positief', label: 'Positief' }, { value: 'negatief', label: 'Negatief' }, { value: 'neutraal', label: 'Neutraal' }]} />
            <CustomSelect value={urgF} onChange={setUrgF} options={[{ value: 'all', label: 'Urgentie' }, { value: 'hoog', label: 'Hoog' }, { value: 'middel', label: 'Middel' }, { value: 'laag', label: 'Laag' }]} />
          </div>

          {/* Articles */}
          {filtered.map(a => (
            <div key={a.article_id} className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-2 hover:border-[#1F3A34]/15 transition-colors" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium bg-[#E6EFEA] text-[#1F3A34]">{a.theme}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium" style={{ backgroundColor: sentBg[a.sentiment], color: sentColor[a.sentiment] }}>{a.sentiment}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-lg font-sans font-medium" style={{ backgroundColor: urgBg[a.urgency], color: urgColor[a.urgency] }}>{a.urgency}</span>
                    {a.is_high_priority && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#8A3C3C]/10 text-[#8A3C3C] font-semibold">HIGH PRIORITY</span>}
                  </div>
                  <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold leading-snug">{a.title}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[20px] font-sans font-semibold" style={{ color: sColor(a.impact_score, 75, 60) }}>{a.impact_score}</p>
                  <p className="text-[8px] text-[#8C877F]">IMPACT</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-[#8C877F]">
                <span>{a.source}</span><span>{a.published_at}</span><span>{a.segment}</span>
              </div>
              <p className="text-[12px] text-[#5A5A5A] font-sans">{a.ai_summary}</p>
              {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#1F3A34] font-sans font-medium hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" />{a.source}</a>}
            </div>
          ))}
          <p className="text-[11px] text-[#D9D3C8] font-sans">{filtered.length} artikelen{search || themeF !== 'all' || sentF !== 'all' || urgF !== 'all' ? ' (gefilterd)' : ''}</p>
        </div>

        {/* Right: Sidebar */}
        <div className="space-y-5">
          {/* Sentiment per Theme */}
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-3">Sentiment per Thema</p>
            {Object.entries(themeSent).sort((a, b) => b[1].count - a[1].count).slice(0, 8).map(([theme, s]) => (
              <div key={theme} className="flex items-center gap-2 py-1.5 border-b border-[#F4F1EB] last:border-0">
                <span className="text-[11px] text-[#5A5A5A] font-sans w-32 truncate capitalize">{theme}</span>
                <div className="flex-1 h-2 bg-[#F4F1EB] rounded-full flex overflow-hidden">
                  {s.pos > 0 && <div className="h-full bg-[#2E6F57]" style={{ width: `${s.pos / s.count * 100}%` }} />}
                  {s.neu > 0 && <div className="h-full bg-[#D9D3C8]" style={{ width: `${s.neu / s.count * 100}%` }} />}
                  {s.neg > 0 && <div className="h-full bg-[#8A3C3C]" style={{ width: `${s.neg / s.count * 100}%` }} />}
                </div>
                <span className="text-[10px] text-[#8C877F] w-4 text-right">{s.count}</span>
              </div>
            ))}
          </div>

          {/* Meest Geraakte Segmenten */}
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-3">Meest Geraakte Segmenten</p>
            {(() => {
              const segs = {};
              (data.articles || []).forEach(a => { segs[a.segment] = (segs[a.segment] || 0) + 1; });
              return Object.entries(segs).sort((a, b) => b[1] - a[1]).map(([seg, count]) => (
                <div key={seg} className="flex items-center justify-between py-1.5 border-b border-[#F4F1EB] last:border-0">
                  <span className="text-[12px] text-[#1C1C1C] font-sans capitalize">{seg}</span>
                  <span className="text-[12px] font-sans font-semibold text-[#1F3A34]">{count}</span>
                </div>
              ));
            })()}
          </div>

          {/* Bronnen */}
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-3">Actieve Bronnen ({data.sources?.length})</p>
            <div className="flex flex-wrap gap-1.5">
              {(data.sources || []).map(s => (
                <span key={s} className="text-[10px] px-2 py-1 rounded-lg bg-[#F4F1EB] text-[#5A5A5A] font-sans">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
